/* eslint react/prop-types: 0 */
import clsx from 'clsx';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { ArrowForward as ArrowForwardIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import Link from '../../links/Link';
import { useEncodedFilterState } from '../../../stores/FilterStateStore';
import { getUrlParamFromEncodedState } from '../../../utilities/FilterStateUrlUtilities';
import PerformanceChart from '../performance-chart/PerformanceChart';
import { getPerfChartDef, pathPerformanceDef } from '../performance-chart/defs';
import MinMaxMean from '../../min-max-mean/MinMaxMean';
import { getMinMaxMeanDef } from '../../min-max-mean/get-min-max-mean';

const useStyles = makeStyles((theme) => ({
    fadeOnHoverTrigger: {
        '&:hover $fadeOnHover': {
            opacity: 1,
        },
    },
    fadeOnHover: {
        transitionProperty: 'opacity',
        transitionDuration: '0.2s',
        transitionTimingFunction: 'ease',
        opacity: 0,
    },
    static: {
        ...theme.mixins.flex.grow(0),
        ...theme.mixins.flex.shrink(0),
    },
    grow: {
        ...theme.mixins.flex.grow(1),
        ...theme.mixins.flex.shrink(1),
        ...theme.mixins.flex.basis('50%'),
    },
    topLine: {
        ...theme.mixins.flex.displayFlex,
        width: '100%',
        paddingBottom: theme.spacing(1),
    },
    title: {
        fontSize: 14,
        fontWeight: 'bold',
        paddingBottom: 12,
    },
    linkContainer: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.justifyContentFlexEnd,
        ...theme.mixins.flex.grow(1),

        '& .MuiIconButton-root': {
            marginRight: theme.spacing(1),

            '&:hover': {
                background: 'none',
            },
        },
    },
    fakeLinkButton: {
        height: '40px',
        padding: theme.spacing(1),
    },
    '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
    linkButton: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.alignItemsCenter,
        color: theme.palette.text.primary,
        textTransform: 'uppercase',
        borderRadius: 4,
        padding: theme.spacing(1),
        animation: '$fadeIn 1s',

        '& svg': {
            fill: theme.palette.text.secondary,
        },

        '&:hover': {
            textDecoration: 'none',
            background: theme.palette.action.hover,
        },
    },
}));

export function TestLogLink({ test, agent, showPath }) {
    const classes = useStyles();
    const [encodedFilterState] = useEncodedFilterState();
    const encoded = { ...encodedFilterState };
    const testLogTooltip = t`View Test Log`;
    const buttonText = showPath ? t`Test/Path Log` : t`Test Log`;
    if (test) encoded.Test = test;
    if (agent) encoded.Agent = agent;
    const url = `/test-log?${getUrlParamFromEncodedState(encoded)}`;
    return (
        <Link className={classes.linkButton} to={url} title={i18n._(testLogTooltip)}>
            {i18n._(buttonText)} <ArrowForwardIcon />
        </Link>
    );
}

const TopLine = ({ title, testLogLink, semLink }) => {
    const classes = useStyles();

    if (!title && !testLogLink && !semLink) return null;

    return (
        <div className={classes.topLine}>
            {title}
            <div className={clsx(classes.linkContainer, classes.fadeOnHover)}>
                {semLink}
                {testLogLink}
            </div>
        </div>
    );
};

export default function PerformanceMinMax({
    showPathPerformance,
    title,
    test,
    chartData,
    tableData,
    testLogLink,
    semLink,
}) {
    const classes = useStyles();
    const chartDef = getPerfChartDef(test);
    // The last param lets us know if we are on the test log or not (if empty, we are on the test log)
    const minMaxMeanDef = getMinMaxMeanDef(tableData, test, testLogLink);

    return (
        <div className={clsx('flex-container', classes.fadeOnHoverTrigger)}>
            <div className={classes.static}>
                <TopLine title={title} testLogLink={testLogLink} semLink={semLink} />
                <MinMaxMean def={minMaxMeanDef} data={tableData} />
            </div>

            <div className={clsx('flex-container', classes.grow)}>
                <PerformanceChart chartDef={chartDef} data={chartData} />
            </div>

            {showPathPerformance && (
                <div className={clsx(classes.title, classes.static)}>
                    <Trans>Path Performance</Trans>
                </div>
            )}

            {showPathPerformance && (
                <div className={clsx('flex-container', classes.grow)}>
                    <PerformanceChart chartDef={pathPerformanceDef} data={chartData} />
                </div>
            )}
        </div>
    );
}

PerformanceMinMax.defaultProps = {
    chartHeight: 150,
};
